// src/portals/creator/pages/DashboardPage.js
import React from "react";
import { useAuth } from "../../../context/AuthContext";
import StatsWidget from "../components/StatsWidget";
import ActivityItem from "../components/ActivityItem";
import OpportunityCard from "../components/OpportunityCard";
import QuickActions from "../components/QuickActions";
import MilestoneWidget from "../components/MilestoneWidget";
import TopFansWidget from "../components/TopFansWidget";
import { DollarSign, Users, Eye, Zap } from "lucide-react";

const DashboardPage = () => {
  const { currentUser: creator } = useAuth();

  if (!creator) {
    return <div>Loading dashboard...</div>;
  }

  const { fullName, dashboardStats = {}, activityFeed = [], opportunityFeed = [] } = creator;
  
  // Safe access for stats
  const revenue = dashboardStats.revenue || { value: "0 ETB", change: "+0%" };
  const subscribers = dashboardStats.subscribers || { value: "0", change: "+0%" };
  const views = dashboardStats.views || { value: "0", change: "+0%" };
  const engagement = dashboardStats.engagement || { value: "0%", change: "+0%" };

  return (
    <div className="space-y-6 max-w-full overflow-hidden">
      {/* Header */}
      <div className="flex flex-col @md:flex-row justify-between items-start @md:items-end gap-2 @md:gap-4">
          <div>
            <h1 className="text-xl @sm:text-3xl font-bold text-slate-800">
                Welcome, {fullName.split(" ")[0]}!
            </h1>
            <p className="text-slate-500 mt-0.5 text-xs @sm:text-base">
                You're making great progress today.
            </p>
          </div>
          <div className="flex flex-col items-start @md:items-end">
              <div className="text-[10px] @sm:text-sm font-medium text-slate-500 uppercase tracking-wider">Current Balance</div>
              <div className="text-xl @sm:text-2xl font-bold text-emerald-600">{revenue.value}</div>
          </div>
      </div>

      {/* Main Layout - Forces stacking inside mobile frame until container is wide enough (@lg) */}
      <div className="flex flex-col @lg:grid @lg:grid-cols-3 gap-6">
          
          {/* Left Column: Stats & Main Content */}
          <div className="@lg:col-span-2 space-y-6 min-w-0">
              {/* Gamification Banner */}
              <MilestoneWidget currentSubscribers={parseInt(subscribers.value.replace(/,/g, '')) || 2740} />

              {/* Mobile-only Quick Actions (Promoted) */}
              <div className="@lg:hidden">
                <QuickActions />
              </div>

              {/* Stats Grid - Responds to container width */}
              <div className="grid grid-cols-2 @sm:grid-cols-4 gap-3 @sm:gap-4">
                <StatsWidget
                    icon={DollarSign}
                    title="Revenue"
                    value={revenue.value}
                    change={revenue.change}
                />
                <StatsWidget
                    icon={Users}
                    title="Subscribers"
                    value={subscribers.value}
                    change={subscribers.change}
                />
                <StatsWidget
                    icon={Eye}
                    title="Page Views"
                    value={views.value}
                    change={views.change}
                />
                <StatsWidget
                    icon={Zap}
                    title="Engagement"
                    value={engagement.value}
                    change={engagement.change}
                />
              </div>

              {/* Activity Feed */}
              <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                <h2 className="text-lg font-bold text-slate-800 mb-4">
                    Recent Activity
                </h2>
                <ul className="space-y-4">
                    {activityFeed.map((item) => (
                    <ActivityItem key={item.id} item={item} />
                    ))}
                </ul>
              </div>
          </div>

          {/* Right Column: Key Actions & Opportunities */}
          <div className="space-y-6 min-w-0">
              {/* Desktop-only Quick Actions */}
              <div className="hidden @lg:block">
                <QuickActions />
              </div>
              
              <TopFansWidget />

              <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                <h2 className="text-lg font-bold text-slate-800 mb-4">
                    Explore Opportunities
                </h2>
                <div className="space-y-3">
                    {opportunityFeed.map((opp) => (
                    <OpportunityCard key={opp.id} opp={opp} />
                    ))}
                </div>
              </div>
          </div>
      </div>
    </div>
  );
};

export default DashboardPage;
